import ko from 'knockout';
import _ from 'underscore';

export default class ViewModel {
  constructor({
    data,
    i18n,
    deps,
  }) {
    this.expressionOperators = [
      {
        value: 'EqualsTo',
        text: i18n.getString('UnifiedEventTracking_Goal_Creation_Type_Option_EqualsTo'),
      },
      {
        value: 'BeginsWith',
        text: i18n.getString('UnifiedEventTracking_Goal_Creation_Type_Option_BeginsWith'),
      },
      {
        value: 'Contains',
        text:
        i18n.getString('UnifiedEventTracking_Goal_Creation_Type_Option_Contains'),
      },
      {
        value: 'RegularExpression',
        text: i18n.getString('UnifiedEventTracking_Goal_Creation_Type_Option_RegularExpression'),
      },
    ];

    this.valueOperators = [
      {
        value: 'EqualTo',
        text: i18n.getString('UnifiedEventTracking_Goal_Creation_Type_Option_EqualsTo'),
      },
      {
        value: 'LessThan',
        text: i18n.getString('UnifiedEventTracking_Goal_Creation_Type_Option_LessThan'),
      },
      {
        value: 'GreaterThan',
        text: i18n.getString('UnifiedEventTracking_Goal_Creation_Type_Option_GreaterThan'),
      },
    ];

    const { schemaCampaignVnext, validator } = deps;
    const opportunity = _.first(data.Opportunities);

    const goalTypeId = opportunity.GoalTypeId;
    const issueTypeId = opportunity.IssueType;
    const goalValueSourceId =
      opportunity.RevenueValue && opportunity.RevenueValue.GoalValueSourceId;
    if (goalTypeId === 0 && opportunity.DestUrl) { // Goal type is Destination URL
      const s$DestinationGoal = schemaCampaignVnext.default.DestinationGoal;
      this.maxUrlLength = s$DestinationGoal.properties.UrlString.maxLength;
      const suggestedSetting = opportunity.DestUrl.SuggestedSetting;

      this.urlString = ko.observable(suggestedSetting.DestinationUrl).extend({
        validate: [validator.required(), validator.string.xss()],
      });
      this.operator = ko.observable(suggestedSetting.DestinationURLOperatorCode).extend({
        validate: [validator.required(), validator.string.xss()],
      });

      this.operatorDescription = ko.computed(() => {
        if (this.operator() === 'RegularExpression') {
          return `${i18n.getString(`UnifiedEventTracking_Goal_Creation_Type_Destination_${this.operator()}`)
          } <a target="_blank" href="https://help.bingads.microsoft.com/#apex/3/en/56715/2">${i18n.getString('UnifiedEventTracking_LearnMore')}</a>`;
        } else if (this.operator()) {
          return `${i18n.getString(`UnifiedEventTracking_Goal_Creation_Type_Destination_${this.operator()}`)
          } <a target="_blank" href="https://help.bingads.microsoft.com/#apex/3/en/56689/2">${i18n.getString('UnifiedEventTracking_LearnMore')}</a>`;
        }
        return '';
      }, this);

      this.isValid = ko.computed(() =>
        this.urlString.isValid() &&
        this.operator.isValid(), this);

      this.allParamIdentical = () => {
        const {
          DestinationURLOperatorCode,
          DestinationUrl,
        } = opportunity.DestUrl.CurrentSetting;
        const currentValues = DestinationURLOperatorCode + DestinationUrl;
        const suggestedValues = this.operator() + this.urlString();
        return currentValues === suggestedValues;
      };

      this.warningMessage = ko.computed(() => {
        if (this.allParamIdentical()) {
          return i18n.getString('Details_View_Suggested_Setting_NoChangeWarning');
        }
        return '';
      }, this);
    } else if (goalTypeId === 3 && opportunity.Event) { // Goal type is Event
      const s$EventGoal = schemaCampaignVnext.default.EventGoal;
      const suggestedSetting = opportunity.Event.SuggestedSetting;

      this.category = ko.observable(suggestedSetting.Category).extend({
        validate: [validator.string.xss()],
      });
      this.categoryOperator = ko.observable(suggestedSetting.CategoryOperatorCode);

      this.action = ko.observable(suggestedSetting.Action).extend({
        validate: [validator.string.xss()],
      });
      this.actionOperator = ko.observable(suggestedSetting.ActionOperatorCode);

      this.label = ko.observable(suggestedSetting.Label).extend({
        validate: [validator.string.xss()],
      });
      this.labelOperator = ko.observable(suggestedSetting.LabelOperatorCode);

      this.value = ko.observable(suggestedSetting.Value).extend({
        validate: [
          validator.string.number(),
          validator.string.number.range(0, 9.22e14),
          validator.string.number.size(7, 0),
        ],
      });
      this.valueOperator = ko.observable(suggestedSetting.ValueOperatorCode);

      this.eventParams = [
        {
          value: this.category,
          operator: this.categoryOperator,
          text: i18n.getString('UnifiedEventTracking_Goal_Creation_Type_Event_Category'),
          maxLength: s$EventGoal.properties.Category.maxLength,
          errorMessage: i18n.getString('Validation_String_Invalid_Characters'),
          isNumeric: false,
          operators: this.expressionOperators,
        },
        {
          value: this.action,
          operator: this.actionOperator,
          text: i18n.getString('UnifiedEventTracking_Goal_Creation_Type_Event_Action'),
          maxLength: s$EventGoal.properties.Action.maxLength,
          errorMessage: i18n.getString('Validation_String_Invalid_Characters'),
          isNumeric: false,
          operators: this.expressionOperators,
        },
        {
          value: this.label,
          operator: this.labelOperator,
          text: i18n.getString('UnifiedEventTracking_Goal_Creation_Type_Event_Label'),
          maxLength: s$EventGoal.properties.Label.maxLength,
          errorMessage: i18n.getString('Validation_String_Invalid_Characters'),
          isNumeric: false,
          operators: this.expressionOperators,
        },
        {
          value: this.value,
          operator: this.valueOperator,
          text: i18n.getString('UnifiedEventTracking_Goal_Creation_Type_Event_Value'),
          maxLength: null,
          errorMessage: i18n.getString('Audiences_Manage_Error_InvalidAudienceCustomEventValue_Number'),
          isNumeric: true,
          operators: this.valueOperators,
        },
      ];

      this.allParamEmpty = () => {
        const str = this.category() + this.action() + this.label() + this.value();
        return _.isString(str) ? _.isEmpty(str.trim()) : true;
      };

      this.allParamIdentical = () => {
        const {
          CategoryOperatorCode,
          Category,
          ActionOperatorCode,
          Action,
          LabelOperatorCode,
          Label,
          ValueOperatorCode,
          Value,
        } = opportunity.Event.CurrentSetting;
        const currentValues = [];
        const suggestedValues = [];
        if (!_.isNull(Category)) {
          currentValues.push(CategoryOperatorCode);
          suggestedValues.push(this.categoryOperator());
        }
        currentValues.push(Category);
        suggestedValues.push(this.category());
        if (!_.isNull(Action)) {
          currentValues.push(ActionOperatorCode);
          suggestedValues.push(this.actionOperator());
        }
        currentValues.push(Action);
        suggestedValues.push(this.action());
        if (!_.isNull(Label)) {
          currentValues.push(LabelOperatorCode);
          suggestedValues.push(this.labelOperator());
        }
        currentValues.push(Label);
        suggestedValues.push(this.label());
        if (!_.isNull(Value)) {
          currentValues.push(ValueOperatorCode);
          suggestedValues.push(this.valueOperator());
        }
        currentValues.push(Value);
        suggestedValues.push(this.value());
        return currentValues.toString() === suggestedValues.toString();
      };

      this.isValid = ko.computed(() =>
        !this.allParamEmpty() &&
        this.category.isValid() &&
        this.action.isValid() &&
        this.label.isValid() &&
        this.value.isValid(), this);

      this.errorMessage = ko.computed(() => {
        if (this.allParamEmpty()) {
          return i18n.getString('UnifiedEventTracking_Goal_Creation_Type_EventNoParamError');
        }
        return '';
      }, this);

      this.warningMessage = ko.computed(() => {
        if (this.allParamIdentical()) {
          return i18n.getString('Details_View_Suggested_Setting_NoChangeWarning');
        }
        return '';
      }, this);
    } else if (issueTypeId === 4 && opportunity.RevenueValue
        && (goalValueSourceId === 1 || goalValueSourceId === 3)) {
      this.inputRevenueValue = ko.observable(1).extend({
        validate: [
          validator.string.number(),
          validator.string.number.range(0, 9.22e14),
          validator.string.number.size(7, 0),
        ],
      });
      this.text = i18n.getString('UnifiedEventTracking_Goal_Creation_Type_Event_Value');
      this.errorMessage = i18n.getString('Audiences_Manage_Error_InvalidAudienceCustomEventValue_Number');
      this.isValid = ko.computed(() =>
        this.inputRevenueValue.isValid(), this);
    }
  }
}
